/**
 * @cfleet/auth - useTenant hook
 *
 * Hook for managing multi-tenant organization context.
 * Allows users to switch between organizations they belong to.
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../AuthProvider';
import type { Organization } from '../types';

/**
 * Return type for useTenant hook
 */
export interface UseTenantReturn {
  /** Current active organization */
  currentOrganization: Organization | null;
  /** List of organizations the user belongs to */
  organizations: Organization[];
  /** Whether the user belongs to multiple organizations */
  isMultiTenant: boolean;
  /** Switch to a different organization */
  switchOrganization: (organizationId: string) => void;
  /** Get organization by ID */
  getOrganization: (organizationId: string) => Organization | undefined;
  /** Check if user belongs to a specific organization */
  belongsToOrganization: (organizationId: string) => boolean;
}

/**
 * Hook for managing multi-tenant organization context
 *
 * @example
 * ```tsx
 * function OrganizationSwitcher() {
 *   const {
 *     currentOrganization,
 *     organizations,
 *     isMultiTenant,
 *     switchOrganization,
 *   } = useTenant();
 *
 *   if (!isMultiTenant) {
 *     return null;
 *   }
 *
 *   return (
 *     <select
 *       value={currentOrganization?.id}
 *       onChange={(e) => switchOrganization(e.target.value)}
 *     >
 *       {organizations.map((org) => (
 *         <option key={org.id} value={org.id}>
 *           {org.name}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export function useTenant(): UseTenantReturn {
  const { currentOrganization, organizations, switchOrganization: switchOrgFn } = useAuth();

  const isMultiTenant = useMemo(() => organizations.length > 1, [organizations]);

  const getOrganization = useCallback(
    (organizationId: string): Organization | undefined => {
      return organizations.find((org) => org.id === organizationId);
    },
    [organizations]
  );

  const belongsToOrganization = useCallback(
    (organizationId: string): boolean => {
      return organizations.some((org) => org.id === organizationId);
    },
    [organizations]
  );

  const switchOrganization = useCallback(
    (organizationId: string) => {
      const org = getOrganization(organizationId);
      if (org) {
        switchOrgFn(organizationId).catch((err) => {
          console.warn(`Failed to switch organization: ${err.message}`);
        });
      } else {
        console.warn(`Organization ${organizationId} not found in user's organizations`);
      }
    },
    [getOrganization, switchOrgFn]
  );

  return {
    currentOrganization,
    organizations,
    isMultiTenant,
    switchOrganization,
    getOrganization,
    belongsToOrganization,
  };
}
